import { useContext, createContext, PropsWithChildren, useState, useEffect, useCallback } from 'react';
import ItemDto from '../models/ItemDto';
import CardItem from '../models/Carditem';
import { useSessionStorageState } from '../hooks/useSessionStorageState';
import { SelectedItemOptions } from '../components/dialogs/itemOptionsDialog/ItemOptionsDialog';
import { useRestaurantData } from './RestaurantDataContext';
import { useLanguage } from './LanguageContext';
import ItemOptionGroupDto from '../models/ItemOptionGroupDto';

interface CartContentContextInterface {
    cardItems: CardItem[],
    addItemToCart: (item: ItemDto, selectedOptions?: SelectedItemOptions) => void,
    removeItemFromCart: (item: CardItem) => void,
    itemsInCart: number,
    clearCart: () => void,
    updateItem: (item: ItemDto, selectedOptions?: SelectedItemOptions, amount?: number) => void,
}

const storageKey = "qr-menu.cart-content";

const CardContentContext = createContext<CartContentContextInterface>({ updateItem: () => { }, cardItems: [], addItemToCart: () => { }, removeItemFromCart: () => { }, itemsInCart: 0, clearCart: () => { } });

export const useCardContent = () => useContext(CardContentContext);

export function CardContentProvider(props: PropsWithChildren<{}>) {
    const { restaurantData } = useRestaurantData();
    const { language } = useLanguage();
    const [updateCartItems, setUpdateCartItems] = useState(0);
    const [cardItems, setCardItems] = useSessionStorageState<CardItem[]>(storageKey, []);
    const [itemsInCart, setItemsInCart] = useState<number>(cardItems.length);

    useEffect(() => {
        setUpdateCartItems(1);
    }, [language]);

    useEffect(() => {
        setUpdateCartItems(oldVal => oldVal === 1 ? 2 : 0);
    }, [restaurantData]);

    const itemInCart = useCallback((item: ItemDto, selectedOptions?: SelectedItemOptions): number => {
        for (let index = 0; index < cardItems.length; index++) {
            const cardItem = cardItems[index];
            if (cardItem.id === item.id) {
                // same item found
                if (!selectedOptions) {
                    // No options to compare
                    return index;
                }
                let itemExtrasAndOptionsMatch: boolean = true;
                if (selectedOptions.itemExtras.length === cardItem.selectedOptions.itemExtras.length && selectedOptions.itemOptionGroups.length === cardItem.selectedOptions.itemOptionGroups.length) {
                    for (let index = 0; index < selectedOptions.itemExtras.length; index++) {
                        const itemExtra = selectedOptions.itemExtras[index];
                        const itemExtraFromCart = cardItem.selectedOptions.itemExtras.filter(itemExtraCart => itemExtraCart.id === itemExtra.id)[0];
                        if (!(itemExtraFromCart && itemExtra.amount === itemExtraFromCart.amount)) {
                            itemExtrasAndOptionsMatch = false;
                            break;
                        }
                    }
                    for (let index = 0; index < selectedOptions.itemOptionGroups.length && itemExtrasAndOptionsMatch; index++) {
                        const selectedOptionGroup = selectedOptions.itemOptionGroups[index];
                        const optionGroupFromCart = cardItem.selectedOptions.itemOptionGroups.filter(itemOptionCart => itemOptionCart.id === selectedOptionGroup.id)[0];
                        if (!(optionGroupFromCart && selectedOptionGroup.itemOptions[0].id === optionGroupFromCart.itemOptions[0].id)) {
                            itemExtrasAndOptionsMatch = false;
                        }
                    }
                } else {
                    itemExtrasAndOptionsMatch = false;
                }
                if (itemExtrasAndOptionsMatch) {
                    return index;
                }
            }
        }
        return -1;
    }, [cardItems]);

    const removeItemFromCart = useCallback((item: CardItem) => {
        let cartItemsNew: CardItem[] = cardItems;
        const indexInCart = itemInCart(item, item.selectedOptions);
        if (indexInCart >= 0) {
            const itemInCart = cardItems[indexInCart];
            itemInCart.quantity--;
            if (itemInCart.quantity === 0) {
                cartItemsNew = cardItems.filter((_cardItem, index) => index !== indexInCart);
            }
        }
        setCardItems([...cartItemsNew]);
    }, [cardItems, setCardItems, itemInCart]);

    const updateCardItem = useCallback((item: ItemDto, indexOfItemInCart: number) => {
        const getItemExtra = (item: ItemDto, itemExtraId: number) => {
            return item.itemExtras.filter(extra => extra.id === itemExtraId)[0];
        }
        const getItemOptionGroup = (item: ItemDto, itemOptionGroupId: number) => {
            return item.itemOptionGroups.filter(group => group.id === itemOptionGroupId)[0];
        }
        const getItemOption = (itemOptiongroup: ItemOptionGroupDto, itemOptionId: number) => {
            return itemOptiongroup.itemOptions.filter(option => option.id === itemOptionId)[0];
        }
        return {
            ...cardItems[indexOfItemInCart], ...item, selectedOptions: {
                itemExtras: cardItems[indexOfItemInCart].selectedOptions.itemExtras?.map(extra => {
                    return {
                        ...extra, ...getItemExtra(item, extra.id)
                    }
                }),
                itemOptionGroups: cardItems[indexOfItemInCart].selectedOptions.itemOptionGroups?.map(group => {
                    const optionGroup = getItemOptionGroup(item, group.id);
                    return {
                        ...group, ...optionGroup, itemOptions: group.itemOptions.map(option => {
                            return { ...option, ...getItemOption(optionGroup, option.id) }
                        })
                    }
                })
            }
        };
    }, [cardItems]);

    const updateItem = useCallback((item: ItemDto, selectedOptions?: SelectedItemOptions, amount?: number) => {
        const indexOfItemInCart = itemInCart(item);
        if (indexOfItemInCart < 0) return;
        const updatedItem = { ...cardItems[indexOfItemInCart], ...item, selectedOptions: selectedOptions ? selectedOptions : cardItems[indexOfItemInCart].selectedOptions, quantity: amount ? amount : cardItems[indexOfItemInCart].quantity };
        console.log('updateItem', updatedItem, selectedOptions)
        const indexOfWholeItemInCart = itemInCart(item, selectedOptions);
        if (indexOfWholeItemInCart < 0) {
            cardItems[indexOfItemInCart] = updatedItem;
        } else {
            cardItems[indexOfItemInCart] = updateCardItem(item, indexOfItemInCart);
        }
        setCardItems([...cardItems]);
    }, [cardItems, itemInCart, setCardItems, updateCardItem]);

    useEffect(() => {
        if (updateCartItems === 2 && cardItems.length > 0 && restaurantData.categories) {
            setUpdateCartItems(0);
            const allItems: Set<ItemDto> = new Set();
            restaurantData.categories.forEach(c => c.subCategories.forEach(s => s.items.forEach(item => allItems.add(item))));
            cardItems.forEach(cardItem => {
                const item = Array.from(allItems.values()).filter(item => item.id === cardItem.id)[0];
                if (item) {
                    updateItem(item);
                } else {
                    removeItemFromCart(cardItem);
                }
            })
            /*setCardItems((oldVal) => oldVal.map(val=>{
                const item = Array.from(allItems.values()).filter(item=>item.id===val.id)[0];
                return {...val,value:item.value,itemExtras:val.itemExtras.map(extra=>{return {...extra,value:item.itemExtras.value}})}
            }));*/
        }
    }, [updateCartItems, setCardItems, restaurantData, cardItems, removeItemFromCart, updateItem])

    const addItemToCart = (item: ItemDto, selectedOptions?: SelectedItemOptions) => {
        //console.log('addItemToCart', item);
        const indexInCart = itemInCart(item, selectedOptions);
        if (indexInCart >= 0) {
            //console.log('is in Cart', item);
            cardItems.filter((_cardItem, index) => indexInCart === index)[0].quantity++;
            setCardItems([...cardItems]);
        } else {
            selectedOptions = selectedOptions ?? { itemExtras: [], itemOptionGroups: [] };
            setCardItems([...cardItems, { ...item, quantity: 1, selectedOptions }]);
        }
    }

    const clearCart = () => {
        setCardItems([]);
    }

    useEffect(() => {
        let numberOfItems = 0;
        cardItems.map(cardItem => cardItem.quantity).forEach(quantity => numberOfItems += quantity);
        setItemsInCart(numberOfItems);
    }, [cardItems])


    const value = { cardItems, itemsInCart, addItemToCart, removeItemFromCart, clearCart, updateItem };

    return <CardContentContext.Provider value={value} {...props} />;
}
